/**
 * Create a lead manually (walk-in, phone enquiry, etc.).
 *
 * POST /v1/leads  →  LeadCreationRequest:
 *   { source, sourceReference, customerFirstName, customerLastName,
 *     customerPhoneE164, customerEmail, vehicleOfInterestId,
 *     vehicleMakeRequested, vehicleModelRequested, notes }
 *
 * The backend resolves the customer by phone hash, then email hash, and only
 * creates a new Customer if neither matches — so typing a phone number that
 * already exists attaches the lead to that customer rather than duplicating
 * them. `hasContact()` is a hard server-side guard: at least one of phone or
 * email must be present, which we enforce here too.
 *
 * You can either pick an existing customer (we copy their details into the
 * request) or type a new one.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LookupField from './LookupField';
import { leadsService } from '../lib/services';
import { useCustomerOptions, useVehicleOptions } from '../lib/useDirectory';
import { apiErrorMessage } from '../lib/apiError';

/**
 * Normalise a typed phone number to E.164, which the backend requires (it must
 * start with "+"; a bare local number throws and surfaced as a 500). Defaults
 * to Australia (+61) — this dealership's country — when no "+" is given.
 */
function toE164(raw) {
  if (!raw) return null;
  let p = String(raw).replace(/[\s()\-.]/g, '');   // strip spaces, dashes, dots, parens
  if (!p) return null;
  if (p.startsWith('+')) return p;
  if (p.startsWith('00')) return '+' + p.slice(2); // international 00 prefix
  if (p.startsWith('0')) p = p.slice(1);            // drop AU trunk 0
  return '+61' + p;
}

/** Backend LeadSource enum. */
const SOURCES = [
  ['WALK_IN',              'Walk-in'],
  ['PHONE',                'Phone'],
  ['WEB_FORM',             'Web form'],
  ['EMAIL',                'Email'],
  ['CARSALES',             'Carsales'],
  ['DRIVE',                'Drive'],
  ['CAREXPERT',            'CarExpert'],
  ['AUTOTRADER',           'Autotrader'],
  ['FACEBOOK_MARKETPLACE', 'Facebook Marketplace'],
  ['WHATSAPP',             'WhatsApp'],
  ['SMS',                  'SMS'],
];

export default function NewLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    source: 'WALK_IN',
    customerId: '',          // optional: pick an existing customer
    firstName: '', lastName: '', phone: '', email: '',
    vehicleOfInterestId: '',
    make: '', model: '',
    notes: '',
    // E1 / T020: a walk-in who is about to drive the car does not pass through
    // Inbound and Contacted on the way. Ricky's case was a customer standing at
    // the desk — name and phone, straight into Test Drive.
    straightToTestDrive: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // DEF-136: when the entered phone/email already has an open lead, the server
  // returns that existing lead instead of creating a duplicate. We hold it here
  // to warn the user and offer to open it, rather than falsely reporting a new
  // lead was created.
  const [duplicate, setDuplicate] = useState(null);
  const navigate = useNavigate();
  // The form is long; when a duplicate warning appears, scroll the body back to
  // the top so the user actually sees it instead of it landing below the fold.
  const bodyRef = useRef(null);
  useEffect(() => {
    if (duplicate && bodyRef.current) {
      bodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [duplicate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const customersQ = useCustomerOptions();
  const vehiclesQ  = useVehicleOptions();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /** Selecting an existing customer fills (and locks) the contact fields. */
  const pickCustomer = (id, opt) => {
    if (!id) {
      setForm((f) => ({ ...f, customerId: '', firstName: '', lastName: '', phone: '', email: '' }));
      return;
    }
    // The option's `secondary` is "phone · email"; parse it back out rather
    // than refetching the customer.
    const [phone, email] = String(opt?.secondary || '').split(' · ');
    const [first, ...rest] = String(opt?.primary || '').split(' ');
    setForm((f) => ({
      ...f,
      customerId: id,
      firstName: first || '',
      lastName: rest.join(' '),
      phone: phone && phone.startsWith('+') ? phone : '',
      email: email || (phone && phone.includes('@') ? phone : ''),
    }));
  };

  const existing = !!form.customerId;

  // Mirror the server's hasContact() guard.
  const hasContact = !!(form.phone.trim() || form.email.trim());

  const selectedVehicle = useMemo(
    () => vehiclesQ.options.find(v => v.id === form.vehicleOfInterestId) || null,
    [vehiclesQ.options, form.vehicleOfInterestId],
  );

  const canSubmit = !busy && hasContact;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setDuplicate(null);
    // Normalise + validate the phone before it reaches the E.164-only backend,
    // so a local number (e.g. 0400 000 000) works and an invalid one gets a
    // clear message instead of a 500.
    const phoneE164 = form.phone.trim() ? toE164(form.phone) : null;
    if (form.phone.trim() && !/^\+\d{8,15}$/.test(phoneE164 || '')) {
      setError('Enter a valid phone number, e.g. 0400 000 000 or +61 400 000 000.');
      setBusy(false);
      return;
    }
    try {
      const { lead: created, duplicate: isDuplicate } = await leadsService.createDetailed({
        source: form.source,
        customerFirstName: form.firstName.trim() || null,
        customerLastName:  form.lastName.trim()  || null,
        customerPhoneE164: phoneE164,
        customerEmail:     form.email.trim()     || null,
        vehicleOfInterestId: form.vehicleOfInterestId || null,
        // If a stock vehicle is linked, carry its make/model through as the
        // free-text request too, so the kanban card reads properly.
        vehicleMakeRequested:  form.make.trim()  || null,
        vehicleModelRequested: form.model.trim() || null,
        notes: form.notes.trim() || null,
      });

      // DEF-136: this customer already had an open lead — nothing new was
      // created. Warn and offer to open the existing one instead of pretending
      // a lead was captured (and don't run the straight-to-test-drive shortcut
      // against a lead that may already be further along).
      if (isDuplicate) {
        setDuplicate(created);
        setBusy(false);
        return;
      }
      // The create endpoint always lands a lead at INBOUND — that is the
      // ingestion contract for webhooks and channel signals, and widening it
      // would let any caller place a lead anywhere. A direct-to-stage capture
      // is therefore create-then-advance, which also leaves the move in the
      // stage history where it belongs.
      let final = created;
      if (form.straightToTestDrive && created?.id) {
        try {
          final = await leadsService.updateStage(created.id, 'TEST_DRIVE', {
            reason: 'Walk-in captured directly at test drive',
          }) ?? created;
        } catch (stageErr) {
          // The lead exists and is safe at Inbound; only the shortcut failed.
          // Say so rather than implying nothing was captured.
          setError(apiErrorMessage(stageErr,
            'Lead created, but it could not be moved to Test Drive. It is at Inbound on the board.'));
          setBusy(false);
          onCreated?.(created);
          return;
        }
      }
      onCreated?.(final);
      onClose?.();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create the lead'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="nlm-title" onClick={onClose}>
      <form
        className="modal"
        style={{ width: 'min(560px, 92vw)' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="modal-header">
          <div>
            <div id="nlm-title" style={{ fontSize: 16, fontWeight: 600 }}>New lead</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {form.straightToTestDrive ? 'Enters the pipeline at Test Drive.' : 'Enters the pipeline at Inbound.'}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" ref={bodyRef} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {duplicate && (
            <div style={{
              padding: '10px 12px', borderRadius: 6, fontSize: 13,
              border: '1px solid var(--warning)', background: 'var(--warning-bg)', color: 'var(--warning)',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div>
                <strong>⚠ Duplicate lead.</strong> This phone/email already belongs to a customer
                with an open lead{duplicate.stage ? ` (stage: ${duplicate.stage})` : ''}, so no new
                lead was created. Open the existing one instead.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button" className="btn small primary"
                  onClick={() => { const id = duplicate.id; onClose?.(); if (id) navigate(`/leads/${id}`); }}
                >
                  Open existing lead →
                </button>
                <button type="button" className="btn small" onClick={() => setDuplicate(null)}>
                  Edit details
                </button>
              </div>
            </div>
          )}

          <div className="form-field">
            <label htmlFor="nlm-source">Source</label>
            <select id="nlm-source" value={form.source} onChange={(e) => set('source', e.target.value)}>
              {SOURCES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </div>

          <LookupField
            id="nlm-customer"
            label="Existing customer"
            placeholder="Search by name, phone or email…"
            emptyHint="No customer matches — fill in the details below to create one"
            options={customersQ.options}
            loading={customersQ.loading}
            value={form.customerId || null}
            onChange={pickCustomer}
          />
          <div className="mono text-xs text-muted" style={{ marginTop: -6, lineHeight: 1.6 }}>
            {existing
              ? 'Using this customer’s details. Clear the field to enter someone new.'
              : 'Leave empty for a new customer — we match on phone, then email, before creating one.'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label htmlFor="nlm-first">First name</label>
              <input
                id="nlm-first"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                disabled={existing}
              />
            </div>
            <div className="form-field">
              <label htmlFor="nlm-last">Last name</label>
              <input
                id="nlm-last"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                disabled={existing}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label htmlFor="nlm-phone">Phone</label>
              <input
                id="nlm-phone"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="+61…"
                disabled={existing}
              />
            </div>
            <div className="form-field">
              <label htmlFor="nlm-email">Email</label>
              <input
                id="nlm-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                disabled={existing}
              />
            </div>
          </div>

          {!hasContact && (
            <div className="mono text-xs" style={{ color: 'var(--warning)', marginTop: -6 }}>
              A phone number or an email is required — it's how the AI reaches them.
            </div>
          )}

          <LookupField
            id="nlm-vehicle"
            label="Vehicle of interest (from stock)"
            placeholder="Search by model, stock # or VIN…"
            emptyHint="No vehicle matches that search"
            options={vehiclesQ.options}
            loading={vehiclesQ.loading}
            value={form.vehicleOfInterestId || null}
            onChange={(id, opt) => {
              setForm((f) => {
                if (!id) return { ...f, vehicleOfInterestId: '' };
                // Best-effort split of "2024 Toyota RAV4 Cruiser" → make/model.
                const parts = String(opt?.primary || '').split(' ');
                const maybeYear = /^\d{4}$/.test(parts[0]) ? parts.shift() : null;
                void maybeYear;
                return {
                  ...f,
                  vehicleOfInterestId: id,
                  make:  f.make  || parts[0] || '',
                  model: f.model || parts.slice(1).join(' ') || '',
                };
              });
            }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label htmlFor="nlm-make">Make requested</label>
              <input
                id="nlm-make"
                value={form.make}
                onChange={(e) => set('make', e.target.value)}
                placeholder="Toyota"
              />
            </div>
            <div className="form-field">
              <label htmlFor="nlm-model">Model requested</label>
              <input
                id="nlm-model"
                value={form.model}
                onChange={(e) => set('model', e.target.value)}
                placeholder="RAV4"
              />
            </div>
          </div>
          {selectedVehicle && (
            <div className="mono text-xs text-muted" style={{ marginTop: -6 }}>
              Linked to stock: {selectedVehicle.primary}
            </div>
          )}

          <div className="form-field">
            <label htmlFor="nlm-notes">Notes</label>
            <textarea
              id="nlm-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="What did they ask for?"
            />
          </div>

          {/* E1 / T020: direct-to-stage capture. Deliberately the last field —
              it changes where the lead lands, so it reads after the details
              rather than before them, and the header subtitle echoes the
              choice so it cannot be ticked without noticing. */}
          <label
            className="flex-row"
            style={{ alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '8px 10px',
                     border: '1px solid var(--border-color)', borderRadius: 6,
                     background: form.straightToTestDrive ? 'color-mix(in srgb, var(--accent) 6%, transparent)' : undefined }}
          >
            <input
              type="checkbox"
              checked={form.straightToTestDrive}
              onChange={(e) => set('straightToTestDrive', e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              <span style={{ fontWeight: 500 }}>They're taking a test drive now</span>
              <span className="mono text-xs text-muted" style={{ display: 'block', marginTop: 2 }}>
                Walk-in at the desk. Creates the lead straight at Test Drive — name and phone are enough.
              </span>
            </span>
          </label>

          {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create lead'}
          </button>
        </div>
      </form>
    </div>
  );
}
